import dayjs from "dayjs";
import { getHostname } from "@punchup/utils/hostname";

export interface CityData {
  count: number;
  latitude: number;
  longitude: number;
  $region: string;
}
interface DataItem {
  timestamp: string; // Assuming timestamp is a string, adjust accordingly if it's a Date object
  count: number; // Assuming count is a number
}
interface TimeDataItem {
  time: string;
  count: number;
}

type MediaError = {
  error: string;
};

type GrowthData = {
  lastWeekTotal: number;
  thisWeekTotal: number;
  absoluteChange: number;
  percentChange: number;
} | null; // Allow for null to handle the initial state before data is loaded
type MomGrowthData = {
  lastMonthTotal: number;
  thisMonthTotal: number;
  absoluteChange: number;
  percentChange: number;
} | null; // Allow for null to handle the initial state before data is loaded

const CLOUDFLARE_API_BASE = "https://api.cloudflare.com/client/v4";

/*
 * Fail-closed "could not verify" outcome, shared by the image and video checks.
 * The message is DISTINCT from the genuine-404 "not found" message so a
 * transient Cloudflare problem (rate limit, 5xx, auth, network, missing
 * metadata) is never mislabelled as a deleted asset — last_state stays honest
 * ("unverified, retrying" vs "deleted, re-upload it"). The publish cron retries
 * pending uploads every run, so a transient problem only delays a publish; a
 * persistent one sticks loudly and trips the stale-upload alert rather than
 * silently shipping unverified media.
 */
const couldNotVerify = (
  kind: "image" | "video",
  id: string,
  reason: string,
  error?: unknown
): MediaError => {
  const message = `Could not verify ${kind} with ID: ${id} in Cloudflare (${reason})`;
  if (error !== undefined) {
    console.error(message, error);
  } else {
    console.error(message);
  }
  return { error: message };
};

/*
 * Verify one Cloudflare Image id exists.
 *
 * SERVER context (the secret CLOUDFLARE_ACCOUNT_ID + CLOUDFLARE_IMAGE_TOKEN are
 * in the environment): call the Cloudflare Images API directly. This path is
 * NOT rate limited — it exists for the publish-pending-uploads cron, whose
 * per-batch bursts previously tripped the self-hop route's 10 req / 10 s limit,
 * whereupon the 429 (no `success` field) was misread as "image not found" and
 * falsely failed publishes for images that exist.
 *
 * CLIENT context (browser — the internal comedian/special editors validate on
 * save from a "use client" component): those server-only secrets are absent
 * from the bundle, so a direct call would request accounts/undefined and fail.
 * Route through the app's own /api/getImageMetadata, which performs the lookup
 * server-side. That route IS rate limited, but an interactive save validates
 * only a couple of images, far under the limit.
 *
 * Both paths fail closed: only a genuine 404 is "not found"; any other failure
 * returns the distinct couldNotVerify error.
 */
const verifyImageExists = async (
  id: string
): Promise<MediaError | undefined> => {
  const notFoundMessage = `Img with ID: ${id} was not found in Cloudflare`;

  if (typeof window !== "undefined") {
    try {
      const response = await fetch(
        `${getHostname()}/api/getImageMetadata/?` +
          new URLSearchParams({ image_id: id })
      );
      // A route-level failure (rate-limit 429, 5xx) means the check could not
      // run — fail closed, but do not claim the image is missing.
      if (!response.ok) {
        return couldNotVerify("image", id, `HTTP ${response.status}`);
      }
      // The route returns { success: false, status: 404 } (HTTP 200) for a
      // genuine miss and passes Cloudflare's { success: true, ... } through on a
      // hit. Only an explicit 404 is "not found".
      const body = await response.json();
      if (body?.status === 404) {
        console.error(notFoundMessage);
        return { error: notFoundMessage };
      }
      return undefined;
    } catch (error) {
      return couldNotVerify("image", id, "request failed", error);
    }
  }

  const { CLOUDFLARE_ACCOUNT_ID, CLOUDFLARE_IMAGE_TOKEN } = process.env;
  try {
    const response = await fetch(
      `${CLOUDFLARE_API_BASE}/accounts/${CLOUDFLARE_ACCOUNT_ID}/images/v1/${id}`,
      { headers: { Authorization: `Bearer ${CLOUDFLARE_IMAGE_TOKEN}` } }
    );
    if (response.status === 404) {
      console.error(notFoundMessage);
      return { error: notFoundMessage };
    }
    if (!response.ok) {
      return couldNotVerify("image", id, `HTTP ${response.status}`);
    }
    return undefined;
  } catch (error) {
    return couldNotVerify("image", id, "request failed", error);
  }
};

/*
 * Given an array of Cloudflare Image IDs, validate that they exist/are found.
 * If undefined/null/empty values are passed, they will be skipped.
 * Returns an error object as soon as a genuinely-missing image is found, else
 * undefined. See verifyImageExists for the server-vs-client rationale.
 */
export const checkImages = async (
  image_ids: (string | null | undefined)[]
): Promise<MediaError | undefined> => {
  for (const id of image_ids) {
    if (!id) continue;
    const error = await verifyImageExists(id);
    if (error) return error;
  }
};

/*
 * Verify one Cloudflare Stream (video) id exists. Mirrors verifyImageExists:
 * a direct Stream call on the server (no rate-limited self-hop), a self-hop
 * through /api/getVideoMetadata on the client (where the secrets are absent),
 * and fail-closed semantics where only a real 404 is "not found". See
 * verifyImageExists above for the full rationale.
 */
const verifyVideoExists = async (
  id: string
): Promise<MediaError | undefined> => {
  const notFoundMessage = `Video with ID: ${id} was not found in Cloudflare`;

  if (typeof window !== "undefined") {
    try {
      const response = await fetch(
        `${getHostname()}/api/getVideoMetadata/?` +
          new URLSearchParams({ video_id: id })
      );
      if (!response.ok) {
        return couldNotVerify("video", id, `HTTP ${response.status}`);
      }
      // The route returns { error: "Video not found", status: 404 } (HTTP 200)
      // for a genuine miss and passes Cloudflare's stream body through on a hit.
      const body = await response.json();
      if (body?.status === 404) {
        console.error(notFoundMessage);
        return { error: notFoundMessage };
      }
      return undefined;
    } catch (error) {
      return couldNotVerify("video", id, "request failed", error);
    }
  }

  const { CLOUDFLARE_ACCOUNT_ID, CLOUDFLARE_API_TOKEN } = process.env;
  try {
    const response = await fetch(
      `${CLOUDFLARE_API_BASE}/accounts/${CLOUDFLARE_ACCOUNT_ID}/stream/${id}`,
      { headers: { Authorization: `Bearer ${CLOUDFLARE_API_TOKEN}` } }
    );
    if (response.status === 404) {
      console.error(notFoundMessage);
      return { error: notFoundMessage };
    }
    if (!response.ok) {
      return couldNotVerify("video", id, `HTTP ${response.status}`);
    }
    return undefined;
  } catch (error) {
    return couldNotVerify("video", id, "request failed", error);
  }
};

/*
 * Given an array of Cloudflare Stream (video) IDs, validate that they exist.
 * If undefined/null/empty values are passed, they will be skipped.
 * Returns an error object as soon as a genuinely-missing video is found, else
 * undefined. See verifyVideoExists for the server-vs-client rationale.
 */
export const checkVideos = async (
  video_ids: (string | null | undefined)[]
): Promise<MediaError | undefined> => {
  for (const id of video_ids) {
    if (!id) continue;
    const error = await verifyVideoExists(id);
    if (error) return error;
  }
};

export const transformDataToCityData = (
  data: any[]
): Record<string, CityData> => {
  const cityData: Record<string, CityData> = {};

  data.forEach((item) => {
    // Parse and validate lat/lng to ensure they are not NaN before adding them to cityData
    const latitude = parseFloat(item.latitude);
    const longitude = parseFloat(item.longitude);

    // Skip entries with invalid latitude or longitude
    if (
      !isNaN(latitude) &&
      !isNaN(longitude) &&
      item["$city"] &&
      item["$region"]
    ) {
      const cityName = item["$city"]; // Assuming city names are unique enough for keys
      if (!cityData[cityName]) {
        cityData[cityName] = {
          count: 0, // Initialize the city count
          latitude: latitude,
          longitude: longitude,
          $region: item["$region"],
        };
      }
      cityData[cityName].count += 1; // Increment count
    }
  });

  return cityData;
};

export function filterDataByCurrentYear(data: { created_at: string | null }[]) {
  const counts = Array(12).fill(0);
  const currentYear = dayjs().year();

  const something = data.forEach(({ created_at }) => {
    if (created_at !== null) {
      const date = dayjs(created_at).toDate();

      if (date.getFullYear() === currentYear) {
        counts[date.getMonth()]++;
      }
    }
  });

  return counts;
}

type TicketDataEvent = {
  timestamp: string;
  nameClass: string;
  ShowDate: string;
  $initial_referrer: string;
  $current_url: string;
  $city: string;
  $region: string;
  latitude: string;
  longitude: string;
  ShowVenue: string;
  event: string;
  previousMonthCount: number;
  lastMonthCount: number;
  lastWeekCount: number;
  previousWeekCount: number;
};

type TimeSeriesData = {
  time: string;
  count: number;
};

type ReferrerData = {
  referrer: string;
  count: number;
};

//convert data object into time series data
function aggregateTimeSeriesData(events: TicketDataEvent[]): TimeSeriesData[] {
  const clicksPerDay = new Map<string, number>();

  events.forEach((event) => {
    const date = timestampToDate(event.timestamp);
    const currentCount = clicksPerDay.get(date) || 0;
    clicksPerDay.set(date, currentCount + 1);
  });

  const timeSeriesData: TimeSeriesData[] = Array.from(
    clicksPerDay,
    ([time, count]) => ({ time, count })
  );
  return timeSeriesData;
}

function timestampToDate(timestamp: string): string {
  return dayjs(timestamp).format("YYYY-MM-DD"); // Converts to YYYY-MM-DD format
}

function transformReferralURL(url: string): string {
  const mapping: Record<string, string> = {
    "t.co": "Twitter",
    $direct: "Punchup",
    "facebook.com": "Facebook",
    "instagram.com": "Instagram",
    "tiktok.com": "TikTok",
    "google.m": "Google",
    "punchup.live": "Punchup",
    "youtube.com": "YouTube",
  };

  for (const key in mapping) {
    if (url.includes(key)) {
      return mapping[key];
    }
  }

  return url; // Return original URL if no match is found
}
export function aggregateDataByDimension(
  events: TicketDataEvent[],
  dimensionKey: string
): ReferrerData[] {
  const countsMap = new Map<string, number>();

  events.forEach((event) => {
    const eventRecord = event as Record<string, any>;
    let dimensionValue = eventRecord[dimensionKey] || "Other";

    // Transform the dimensionValue if the dimensionKey is $initial_referrer
    if (dimensionKey === "$initial_referrer") {
      dimensionValue = transformReferralURL(dimensionValue);
    }

    const currentCount = countsMap.get(dimensionValue) || 0;
    countsMap.set(dimensionValue, currentCount + 1);
  });

  const aggregatedData: ReferrerData[] = Array.from(
    countsMap,
    ([referrer, count]) => ({ referrer, count })
  );

  return aggregatedData;
}

type CityInfo = {
  city: string;
  lat: number;
  lng: number;
  count: number;
};

type RegionInfo = {
  region: string;
  cities: CityInfo[];
};

function aggregateCityData(events: TicketDataEvent[]): RegionInfo[] {
  const regionMap = new Map<string, Map<string, CityInfo>>();

  events.forEach((event) => {
    const city = event["$city"];
    const region = event["$region"];
    const latitude = parseFloat(event.latitude);
    const longitude = parseFloat(event.longitude);

    if (!regionMap.has(region)) {
      regionMap.set(region, new Map());
    }

    const cityMap = regionMap.get(region)!; // Non-null assertion here

    if (cityMap.has(city)) {
      const cityInfo = cityMap.get(city)!; // Non-null assertion here
      cityInfo.count += 1;
    } else {
      cityMap.set(city, { city, lat: latitude, lng: longitude, count: 1 });
    }
  });

  const regionData: RegionInfo[] = [];

  regionMap.forEach((cities, region) => {
    const cityData = Array.from(cities.values());
    regionData.push({ region, cities: cityData });
  });

  return regionData;
}

function filterDataByTime(
  events: TicketDataEvent[],
  startDate: string,
  endDate: string
): TicketDataEvent[] {
  const start = dayjs.utc(startDate);
  const end = dayjs.utc(endDate);

  return events.filter((event) => {
    const eventDate = dayjs.utc(event.timestamp);
    return eventDate.isAfter(start) && eventDate.isBefore(end);
  });
}
export function calculateGrowthMetrics(
  events: TicketDataEvent[],
  period: "week" | "month",
  interval: number
): {
  absGrowth: number;
  pctGrowth: number;
  previousPeriodCount: number;
  lastPeriodCount: number;
} {
  const now = dayjs.utc();
  const lastPeriodStart =
    period === "week"
      ? now.subtract(interval, "week")
      : now.subtract(interval, "month");
  const previousPeriodStart =
    period === "week"
      ? lastPeriodStart.subtract(interval, "week")
      : lastPeriodStart.subtract(interval, "month");

  const lastPeriodCount = filterDataByTime(
    events,
    lastPeriodStart.toISOString(),
    now.toISOString()
  ).length;
  const previousPeriodCount = filterDataByTime(
    events,
    previousPeriodStart.toISOString(),
    lastPeriodStart.toISOString()
  ).length;

  const absGrowth = lastPeriodCount - previousPeriodCount;
  const pctGrowth =
    previousPeriodCount > 0 ? absGrowth / previousPeriodCount : absGrowth;

  return { previousPeriodCount, lastPeriodCount, absGrowth, pctGrowth };
}

export function aggregateDayOfWeekData(events: TicketDataEvent[]): any[] {
  const countsPerDayOfWeek = new Map();

  events.forEach((event) => {
    // Format the date to get the full day name
    const dayName = dayjs.utc(event.timestamp).format("dddd");
    const currentCount = countsPerDayOfWeek.get(dayName) || 0;
    countsPerDayOfWeek.set(dayName, currentCount + 1);
  });

  const aggregatedData = Array.from(
    countsPerDayOfWeek,
    ([referrer, count]) => ({
      referrer, // Changed from 'day' to 'referrer'
      count,
    })
  );
  return aggregatedData;
}

function calculateLifetimeTotal(events: TicketDataEvent[]): number {
  return events.length; // Simply returns the total number of events
}
interface NameClassData {
  [key: string]: {
    ShowDate: string[];
  };
}

type DashboardData = {
  eventType: string;
  lifetimeTotal: number;
  wowAbsGrowth: number;
  wowPctGrowth: number;
  currentWeekTotal: number;
  lastWeekTotal: number;
  lifetimeBenchmark: number;
  weekBenchmark: number;
  monthBenchmark: number;
  momAbsGrowth: number;
  momPctGrowth: number;
  currentMonthTotal: number;
  lastThirtyTotal: number;
  referrerData: ReferrerData[];
  cityData: RegionInfo[];
  timeSeries: TimeSeriesData[];
  dayOfWeekData: { referrer: string; count: number }[];
  topVenues: ReferrerData[];
};
export function compileDashboardData(
  ticketsDataResponse: TicketDataEvent[],
  eventType: "Ticket Link Clicks" | "Video Views",
  nameClass?: "Events" | "Videos",
  timeFilter?: "7-Day" | "30-Day" | "Lifetime"
): DashboardData[] {
  // Determine the time range based on timeFilter
  let startDate = "2023-08-31"; // Default start date for 'Lifetime'
  let endDate = dayjs.utc().format(); // Current date as end date

  if (timeFilter === "7-Day") {
    startDate = dayjs.utc().subtract(7, "day").format();
  } else if (timeFilter === "30-Day") {
    startDate = dayjs.utc().subtract(30, "day").format();
  }

  // Apply the time filter to the ticketsDataResponse
  const filteredEvents = ticketsDataResponse.filter((event) => {
    const eventDate = dayjs.utc(event.timestamp);
    return (
      eventDate.isAfter(dayjs.utc(startDate)) &&
      eventDate.isBefore(dayjs.utc(endDate))
    );
  });
  let nameClassData: NameClassData = {};

  // Extract 'ShowDate' from filteredEvents if nameClass is "Events"
  if (nameClass === "Events") {
    ticketsDataResponse.forEach((event) => {
      const showDate = event.ShowDate;
      const showVenue = event.ShowVenue;

      if (showVenue && showDate) {
        if (!nameClassData[showVenue]) {
          nameClassData[showVenue] = { ShowDate: [] };
        }
        // Add the ShowDate value to the array under the ShowDate key
        nameClassData[showVenue].ShowDate.push(showDate);
      }
    });
  }
  // Now use the filteredEvents for further processing
  const timeSeriesData = aggregateTimeSeriesData(filteredEvents);
  const referrerData = aggregateDataByDimension(
    filteredEvents,
    "$initial_referrer"
  );
  const cityData = aggregateCityData(filteredEvents);
  const wowData = calculateGrowthMetrics(ticketsDataResponse, "week", 1);
  const momData = calculateGrowthMetrics(ticketsDataResponse, "month", 1);
  const lifetimeTotal = calculateLifetimeTotal(filteredEvents);
  const dayOfWeekData = aggregateDayOfWeekData(filteredEvents);

  // Determine the dimension for topVenues based on eventType
  const topVenuesDimension =
    eventType === "Ticket Link Clicks" ? "ShowVenue" : "$current_url";
  const topVenues = aggregateDataByDimension(
    filteredEvents,
    topVenuesDimension
  );

  // Creating the dashboard data object
  const dashboardData: DashboardData = {
    eventType,
    lifetimeTotal,
    wowAbsGrowth: wowData.absGrowth,
    wowPctGrowth: wowData.pctGrowth,
    currentWeekTotal: wowData.lastPeriodCount,
    lastWeekTotal: wowData.previousPeriodCount,
    lifetimeBenchmark: 1.2, // Placeholder value
    weekBenchmark: 0.8, // Placeholder value
    monthBenchmark: 1.1, // Placeholder value
    momAbsGrowth: momData.absGrowth,
    momPctGrowth: momData.pctGrowth,
    currentMonthTotal: momData.lastPeriodCount,
    lastThirtyTotal: momData.previousPeriodCount,
    referrerData,
    cityData,
    timeSeries: timeSeriesData,
    dayOfWeekData: dayOfWeekData,
    topVenues: topVenues,
    ...(nameClass
      ? { nameClass: nameClass, nameClassData: nameClassData }
      : {}),
  };
  return [dashboardData];
}

export function groupEventsByDimension(
  tickets: TicketDataEvent[],
  dimension: keyof TicketDataEvent
): Record<string, TicketDataEvent[]> {
  return tickets.reduce(
    (acc, ticket) => {
      const key = ticket[dimension] || "Unknown"; // Fallback for tickets without the specified dimension
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(ticket);
      return acc;
    },
    {} as Record<string, TicketDataEvent[]>
  );
}
